import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';

/**
 * Safe Zone layout helper.
 *
 * Phaser FIT mode keeps the 720×1560 canvas proportional, but every scene
 * places UI in absolute canvas coordinates (y=80 for headers, STAGE_H-100 for
 * bottom CTAs, etc). On notched / Dynamic Island devices those edge UIs sit
 * uncomfortably close to the viewport edge because main.ts only shrinks the
 * outer `#app` parent — the canvas-internal coordinate space is untouched.
 *
 * `getLayoutFrame()` exposes a safe zone in canvas coordinates that:
 *   - falls back to the designed 80px top / 100px bottom padding on
 *     non-notched devices (so the existing layout is unchanged), and
 *   - expands inward when OS insets are larger, so headers and bottom CTAs
 *     are pulled away from the viewport edge.
 *
 * Scenes use `frame.fromTop(originalY)` / `frame.fromBottom(STAGE_H - originalY)`
 * to translate their existing absolute coordinates — minimal code churn.
 *
 * Data flow: main.ts.fitAppToVisualViewport writes 3 values into the Phaser
 * game registry (`safeInsetCssTop`, `safeInsetCssBottom`, `canvasPerCssPx`)
 * every time the visual viewport changes. We read them on demand and convert
 * CSS pixels → canvas pixels via the FIT scale factor.
 */

export interface LayoutFrame {
  /** Top of the safe zone in canvas coordinates. Headers / status pills anchor here. */
  top: number;
  /** Bottom of the safe zone in canvas coordinates. Bottom CTAs anchor here. */
  bottom: number;
  /** bottom - top. Used to scale the hero character relative to available space. */
  height: number;
  /** Vertical center of the safe zone. */
  centerY: number;
  /** Mirror of GAME_CONFIG.STAGE_W for convenience (horizontal layout rarely needs the safe zone). */
  width: number;
  /** Translate a design-time top-anchored y (e.g. 80, 232, 1085) into the safe zone. */
  fromTop: (yFromTop: number) => number;
  /** Translate a design-time bottom-anchored y. Pass `STAGE_H - originalY` for legacy code. */
  fromBottom: (yFromBottom: number) => number;
  /** Lerp inside the safe zone, t=0 → top, t=1 → bottom. */
  lerp: (t: number) => number;
}

export function getLayoutFrame(scene: Phaser.Scene): LayoutFrame {
  const { STAGE_W, STAGE_H, SAFE_TOP_PADDING_CANVAS, SAFE_BOTTOM_PADDING_CANVAS } = GAME_CONFIG;

  const reg = scene.game.registry;
  const cssTop = (reg.get('safeInsetCssTop') as number | undefined) ?? 0;
  const cssBottom = (reg.get('safeInsetCssBottom') as number | undefined) ?? 0;
  const canvasPerCssPx = (reg.get('canvasPerCssPx') as number | undefined) ?? 1;

  const insetTopCanvas = cssTop * canvasPerCssPx;
  const insetBottomCanvas = cssBottom * canvasPerCssPx;

  const top = Math.max(SAFE_TOP_PADDING_CANVAS, insetTopCanvas);
  const bottom = STAGE_H - Math.max(SAFE_BOTTOM_PADDING_CANVAS, insetBottomCanvas);
  const height = Math.max(1, bottom - top);

  return {
    top,
    bottom,
    height,
    centerY: (top + bottom) / 2,
    width: STAGE_W,
    fromTop: (yFromTop) => top + (yFromTop - SAFE_TOP_PADDING_CANVAS),
    fromBottom: (yFromBottom) => bottom - (yFromBottom - SAFE_BOTTOM_PADDING_CANVAS),
    lerp: (t) => top + height * t,
  };
}

/**
 * Multiplier for hero-character base scale that shrinks when the safe zone is
 * smaller than the designed area. Floor at 0.85 so the character never gets
 * uncomfortably small on extreme notches.
 *
 * Apply as: `character.container.setScale(designedScale * getCharacterScaleFactor(frame))`.
 */
export function getCharacterScaleFactor(frame: LayoutFrame): number {
  const { STAGE_H, SAFE_TOP_PADDING_CANVAS, SAFE_BOTTOM_PADDING_CANVAS } = GAME_CONFIG;
  const designedHeight = STAGE_H - SAFE_TOP_PADDING_CANVAS - SAFE_BOTTOM_PADDING_CANVAS;
  const factor = frame.height / designedHeight;
  return Math.max(0.85, Math.min(1, factor));
}
